import { useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { workspaceService } from '../../services/workspace.service';
import { useAuthStore } from '../../stores/auth.store';
import { EntitySelectField } from '../../components/EntitySelectField';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import { selectedWorkspaceId } from '../../stores/workspace.store';

/** Workspace-level settings. Today: the workspace fallback group. Editing is admin-only. */
export default function WorkspacePanel() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const user = useAuthStore((s) => s.user);
  // The selected workspace, else the only one; otherwise the user picks (#297: never the home).
  const [workspaceId, setWorkspaceId] = useState<string | undefined>(() => selectedWorkspaceId() ?? (defaultCreateWorkspace() || undefined));
  const canEdit = user?.role === 'admin';

  const { data: workspace, isLoading, isError } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => workspaceService.getWorkspace(workspaceId!),
    enabled: !!workspaceId,
    retry: false,
  });

  const setFallback = useMutation({
    mutationFn: (groupId: string | null) => workspaceService.updateWorkspace(workspaceId!, { fallback_rule_group_id: groupId }),
    onSuccess: (_data, groupId) => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['rule-groups-library'] });
      enqueueSnackbar(groupId ? 'Workspace fallback set' : 'Workspace fallback cleared', { variant: 'success' });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      enqueueSnackbar(err?.response?.data?.error?.message || 'Could not update the workspace fallback', { variant: 'error' });
    },
  });

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Workspace fallback group</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The global catch-all: when a flow's own rule groups match nothing (or every offer is
        capped or closed), the visitor is served from this group instead of a blank page. It
        must have no conditions.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <WorkspaceField value={workspaceId ?? ''} onChange={setWorkspaceId} />
      </Box>

      {!workspaceId ? null : isLoading && !isError ? (
        <CircularProgress size={20} />
      ) : (
        <EntitySelectField
          field="rule_group"
          label="Fallback group"
          value={workspace?.fallback_rule_group_id ?? null}
          onChange={(id) => setFallback.mutate(id)}
          workspaceId={workspaceId}
          disabled={!canEdit}
          helperText={canEdit
            ? 'Search your rule groups. None — capped traffic gets about:blank.'
            : 'Only workspace admins can change this.'}
        />
      )}
      {!isLoading && workspace && !workspace.fallback_rule_group_id && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No workspace fallback is set — traffic no flow can serve gets a blank page.
        </Alert>
      )}
    </Box>
  );
}
